import { Toggle, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { ConnectorPolicyResponse } from "@/api";
import {
  adminGetConnectorPolicyOptions,
  adminGetConnectorPolicyQueryKey,
  adminListConnectorsQueryKey,
  adminUpdateConnectorPolicyMutation,
  errorMessage,
  getConnectorPolicyQueryKey,
} from "@/api";
import { useAppForm } from "@/components/form/form";
import { PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";

const policySchema = z.object({ usersMayAdd: z.boolean(), allowed: z.string(), blocked: z.string() });
type PolicyValues = z.infer<typeof policySchema>;

const lines = (text: string) =>
  text
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);

function PolicyForm({ policy, canEdit }: Readonly<{ policy: ConnectorPolicyResponse; canEdit: boolean }>) {
  const { t } = useTranslation("adminIntegrations");
  const toast = useToast();
  const queryClient = useQueryClient();
  const save = useMutation(adminUpdateConnectorPolicyMutation());
  const form = useAppForm({
    defaultValues: {
      usersMayAdd: policy.usersMayAdd,
      allowed: (policy.allowedHosts ?? []).join("\n"),
      blocked: (policy.blockedHosts ?? []).join("\n"),
    } as PolicyValues,
    validators: { onChange: policySchema },
    onSubmit: async ({ value }) => {
      try {
        await save.mutateAsync({
          body: {
            usersMayAdd: value.usersMayAdd,
            allowedHosts: lines(value.allowed),
            blockedHosts: lines(value.blocked),
          },
          throwOnError: true,
        });
        await Promise.all(
          [adminGetConnectorPolicyQueryKey(), getConnectorPolicyQueryKey(), adminListConnectorsQueryKey()].map(
            (queryKey) => queryClient.invalidateQueries({ queryKey }),
          ),
        );
        toast.success(t("connectors.policySaved"));
      } catch (error) {
        toast.error(errorMessage(error, t("connectors.policyFailed")));
      }
    },
  });

  if (!canEdit) {
    return (
      <div className="flex flex-col gap-2">
        <Toggle label={t("connectors.usersMayAdd")} checked={policy.usersMayAdd} disabled onChange={() => undefined} />
        <Text variant="body-sm" tone="secondary">
          {t("connectors.allowedHosts")}: {(policy.allowedHosts ?? []).join(", ") || t("connectors.anyHost")}
        </Text>
        <Text variant="body-sm" tone="secondary">
          {t("connectors.blockedHosts")}: {(policy.blockedHosts ?? []).join(", ") || t("connectors.noHosts")}
        </Text>
      </div>
    );
  }

  return (
    <form
      aria-label={t("connectors.policyTitle")}
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <form.AppField name="usersMayAdd">
        {(field) => <field.ToggleField label={t("connectors.usersMayAdd")} />}
      </form.AppField>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-1">
          <form.AppField name="allowed">
            {(field) => (
              <field.TextareaField
                label={t("connectors.allowedHosts")}
                rows={4}
                placeholder={t("connectors.hostPlaceholder")}
              />
            )}
          </form.AppField>
          <Text variant="caption" tone="tertiary">
            {t("connectors.allowedHostsHelp")}
          </Text>
        </div>
        <div className="flex flex-col gap-1">
          <form.AppField name="blocked">
            {(field) => <field.TextareaField label={t("connectors.blockedHosts")} rows={4} />}
          </form.AppField>
          <Text variant="caption" tone="tertiary">
            {t("connectors.blockedHostsHelp")}
          </Text>
        </div>
      </div>
      <form.AppForm>
        <form.SubmitActions onCancel={() => form.reset()} submitLabel={t("connectors.savePolicy")} />
      </form.AppForm>
    </form>
  );
}

/** The org's connector policy (governance, D16): who may add connectors, and which hosts. */
export function ConnectorPolicySection({ canEdit }: Readonly<{ canEdit: boolean }>) {
  const { t } = useTranslation("adminIntegrations");
  const policy = useQuery({ ...adminGetConnectorPolicyOptions(), select: (r) => r.data });
  return (
    <PageSection title={t("connectors.policyTitle")} description={t("connectors.policyBody")}>
      {policy.data && <PolicyForm key={JSON.stringify(policy.data)} policy={policy.data} canEdit={canEdit} />}
    </PageSection>
  );
}
